/* global Office, OfficeRuntime */
const clientId =
    process.env.NODE_ENV === "production"
    ? "0MzSQtBpvEG5ScW3mrcE9CjmE6MevGj4oUsh46Xh"
    : "PbojxW7XXyD27x58Mpbeu8E7IQCOGXLppNvPhNCP"; 
const redirectUri = `${window.location.origin}/auth-callback.html`;
const authentikBaseUrl = "https://auth.winstonwolf-capital.com";

export async function signIn() {
  const { verifier, challenge } = await generatePkcePair();

  // Store verifier temporarily for token exchange
  await OfficeRuntime.storage.setItem("pkceVerifier", verifier);

  const authUrl =
    `${authentikBaseUrl}/application/o/authorize/?` +
    `response_type=code&client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${redirectUri}` +
    `&scope=openid profile email` +
    `&code_challenge=${challenge}` +
    `&code_challenge_method=S256`;

  console.log(authUrl);
  return new Promise<void>((resolve) => {
    Office.context.ui.displayDialogAsync(authUrl, { height: 70, width: 40 }, result => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to open login dialog:", result.error);
        resolve();
        return;
      }

      const dialog = result.value;

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args) => {
        const authCode = args.message;
        dialog.close();

        try {
          const storedVerifier = await OfficeRuntime.storage.getItem("pkceVerifier");

        console.log({
          authCode,
          redirectUri,
          clientId,
          storedVerifier
        });

          const tokenRes = await fetch(`${authentikBaseUrl}/application/o/token/`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              grant_type: "authorization_code",
              code: authCode,
              redirect_uri: redirectUri,
              client_id: clientId,
              code_verifier: storedVerifier || ""   // PKCE step
            })
          });

          if (!tokenRes.ok) {
            const errText = await tokenRes.text();
            console.error(`❌ Token exchange failed: ${tokenRes.status} – ${errText}`);
            const statusEl = document.getElementById("login-status");
            if (statusEl) statusEl.innerText = "Login failed";
            resolve();
            return;
          }

          const tokenData = await tokenRes.json();

          await OfficeRuntime.storage.setItem("authToken", tokenData.access_token);
          if (tokenData.id_token) {
            await OfficeRuntime.storage.setItem("idToken", tokenData.id_token);
          }
          await OfficeRuntime.storage.setItem("tokenExpiry", (Date.now() + tokenData.expires_in * 1000).toString());
          await OfficeRuntime.storage.removeItem("pkceVerifier");

          const statusEl = document.getElementById("login-status");
          if (statusEl) statusEl.innerText = "✅ Logged in";

          resolve();
        } catch (err) {
          console.error("Auth error:", err);
          const statusEl = document.getElementById("login-status");
          if (statusEl) statusEl.innerText = "Login failed";
          resolve();
        }
      });

      dialog.addEventHandler(Office.EventType.DialogEventReceived, e => {
        console.warn("Login dialog closed/cancelled:", e);
        const statusEl = document.getElementById("login-status");
        if (statusEl) statusEl.innerText = "Login cancelled";
        resolve();
      });
    });
  });
}

// --- Logout helper ---
export async function logout() {
  await OfficeRuntime.storage.removeItem("authToken");
  await OfficeRuntime.storage.removeItem("tokenExpiry");
  await OfficeRuntime.storage.removeItem("idToken");

  const statusEl = document.getElementById("login-status");
  if (statusEl) statusEl.innerText = "🔒 Logged out";

  console.log("✅ Tokens cleared locally, logging out from Authentik…");

  const idToken = await OfficeRuntime.storage.getItem("idToken");
  const logoutUrl = 
    process.env.NODE_ENV === "production"
    ? new URL(`${authentikBaseUrl}/application/o/reolyzer-add-in/end-session/`)
    : new URL(`${authentikBaseUrl}/application/o/reolyzer-add-in-test/end-session/`);
  if (idToken) logoutUrl.searchParams.set("id_token_hint", idToken);

  Office.context.ui.displayDialogAsync(
    logoutUrl.toString(),
    { height: 50, width: 50 },
    (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const dialog = result.value;
        // auto-close after 2 seconds
        setTimeout(() => dialog.close(), 2000);
      }
    }
  );
}

export async function isLoggedIn(): Promise<boolean> {
  const token = await OfficeRuntime.storage.getItem("authToken");
  const expiry = parseInt(await OfficeRuntime.storage.getItem("tokenExpiry") || "0", 10);
  return !!token && Date.now() < expiry;
}


export async function getValidToken(): Promise<string | null> {
  const token = await OfficeRuntime.storage.getItem("authToken");
  const expiryStr = await OfficeRuntime.storage.getItem("tokenExpiry");
  const expiry = expiryStr ? parseInt(expiryStr, 10) : 0;

  // If no token or already expired → force login
  if (!token || Date.now() >= expiry) {
    console.warn("⚠️ Token missing or expired. Redirecting to login…");

    try {
      await signIn(); // this handles login and stores new tokens
      // after login, grab the fresh token
      return await OfficeRuntime.storage.getItem("authToken");
    } catch (err) {
      console.error("❌ Login failed:", err);
      return null;
    }
  }

  return token;
}

function base64UrlEncode(str: ArrayBuffer): string {
  return btoa(String.fromCharCode.apply(null, [...new Uint8Array(str)]))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function generatePkcePair() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = base64UrlEncode(array.buffer);

  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = base64UrlEncode(digest);

  return { verifier, challenge };
}
